/**
 * Popup Module
 * Generate info window content for map markers
 * - Generate popup for AWLR stations
 * - Generate popup for ARR stations
 * - Generate popup for AWLR+ARR stations
 * - Generate popup for AWS stations
 * - Generate popup for Flow Meter stations
 *
 * @requires DashboardHelpers
 * @requires DashboardConstants
 */

const PopupModule = (() => {
  // Private methods

  const _isSensorFlagEnabled = (data, flagKey, altKeys = []) => {
    const keys = [flagKey, ...(altKeys || [])];
    return keys.some((key) => data && data[key] === true);
  };

  const _displaySensorValue = (value) =>
    value == null || value === '' ? '-' : value;

  const _displaySensorWithUnit = (value, unit) => {
    const display = _displaySensorValue(value);
    return display === '-' ? '-' : `${display}${unit}`;
  };

  const _toNumberOrNull = (value) => {
    if (value == null || value === '') return null;
    const num = typeof value === 'number' ? value : parseFloat(String(value).trim());
    return Number.isNaN(num) ? null : num;
  };

  const _toIntOrNull = (value) => {
    if (value == null || value === '') return null;
    const num = typeof value === 'number' ? value : parseInt(String(value).trim(), 10);
    return Number.isNaN(num) ? null : num;
  };

  /**
   * Ambil curah hujan (mm) — prioritas field langsung seperti di card.
   * @param {object} data
   * @param {number|null} pipeIndex index di lastData pipe (ARR: null = single value)
   */
  const _resolveRainfallLastHour = (data, pipeIndex = null) => {
    const direct = _toNumberOrNull(
      data.rainfallLastHour ?? data.RainfallLastHour,
    );
    if (direct != null) return direct;

    if (data.lastData == null || data.lastData === '') return null;
    if (typeof data.lastData === 'number') return data.lastData;

    const parts = String(data.lastData)
      .split('|')
      .map((part) => part.trim());

    if (pipeIndex == null) {
      return parts.length ? _toNumberOrNull(parts[0]) : null;
    }

    return _toNumberOrNull(parts[pipeIndex]);
  };

  /**
   * Ambil intensitas hujan — prioritas field langsung seperti di card.
   * @param {object} data
   * @param {number|null} pipeIndex index di status pipe (ARR: null = single value)
   */
  const _resolveIntensityLastHour = (data, pipeIndex = null) => {
    const direct = _toIntOrNull(
      data.intensityLastHour ?? data.IntensityLastHour,
    );
    if (direct != null) return direct;

    if (data.status == null || data.status === '') return null;
    if (typeof data.status === 'number') return data.status;

    const parts = String(data.status)
      .split('|')
      .map((part) => part.trim());

    if (pipeIndex == null) {
      return parts.length ? _toIntOrNull(parts[0]) : null;
    }

    return _toIntOrNull(parts[pipeIndex]);
  };

  const _formatRainfallMm = (value) => {
    if (value == null) return '-';
    const formatted = DashboardHelpers.formatNumber(value);
    return formatted ? `${formatted} <sup>mm</sup>` : '-';
  };

  const _appendWqSensorRow = (label, displayValue) => {
    if (displayValue == null) return '';
    return `
      <tr>
        <td class="py-1 px-0">${label}</td>
        <td class="py-1 px-2">:</td>
        <td class="py-1 px-0">${displayValue}</td>
      </tr>
    `;
  };

  const _buildWqSensorRows = (data) => {
    const unit = data.unitDisplay || data.unit || 'm';
    let rows = '';

    if (_isSensorFlagEnabled(data, 'isTemperature')) {
      rows += _appendWqSensorRow(
        'Suhu',
        _displaySensorWithUnit(data.temperature, ' °C'),
      );
    }

    if (_isSensorFlagEnabled(data, 'isPh', ['isPH'])) {
      rows += _appendWqSensorRow('pH', _displaySensorValue(data.ph));
    }

    if (_isSensorFlagEnabled(data, 'isOrp')) {
      rows += _appendWqSensorRow(
        'ORP',
        _displaySensorWithUnit(data.orp, ' mV'),
      );
    }

    if (_isSensorFlagEnabled(data, 'isTurbidity')) {
      rows += _appendWqSensorRow(
        'Turbidity',
        _displaySensorWithUnit(data.turbidity, ' NTU'),
      );
    }

    if (_isSensorFlagEnabled(data, 'isCod')) {
      rows += _appendWqSensorRow(
        'COD',
        _displaySensorWithUnit(data.cod, ' mg/L'),
      );
    }

    if (_isSensorFlagEnabled(data, 'isSalinity')) {
      rows += _appendWqSensorRow(
        'Salinity',
        _displaySensorWithUnit(data.salinity, ' g/L'),
      );
    }

    if (_isSensorFlagEnabled(data, 'isWaterLevel')) {
      if (DashboardServerConfig.organizationCode === 'ORG007') {
        rows += _appendWqSensorRow(
          'TMA Hulu',
          _displaySensorWithUnit(data.waterLevel1, ` ${unit}`),
        );
        rows += _appendWqSensorRow(
          'TMA Hilir',
          _displaySensorWithUnit(data.waterLevel2, ` ${unit}`),
        );
      } else {
        rows += _appendWqSensorRow(
          'TMA',
          _displaySensorWithUnit(data.waterLevel, ` ${unit}`),
        );
      }
    }

    if (_isSensorFlagEnabled(data, 'isDo')) {
      const doValue = data.do ?? data.Do;
      rows += _appendWqSensorRow(
        'DO',
        _displaySensorWithUnit(doValue, ' mg/L'),
      );
    }

    if (_isSensorFlagEnabled(data, 'isAmmonium')) {
      rows += _appendWqSensorRow(
        'Ammonium NH₄⁺',
        _displaySensorWithUnit(data.ammonium, ' mg/L'),
      );
    }

    if (_isSensorFlagEnabled(data, 'isAmmonia')) {
      rows += _appendWqSensorRow(
        'Ammonia',
        _displaySensorWithUnit(data.ammonia, ' mg/L'),
      );
    }

    return rows;
  };

  /**
   * Generate AWLR popup content
   */
  const _generateAwlrPopup = (data) => {
    moment.locale('id');

    const lastData = (data.lastData || '|-|-').split('|');
    const watershedName = data.watershedName || '-';
    const readingAt = data.lastReading
      ? `${moment.parseZone(data.lastReading).format('D MMMM YYYY HH:mm')} ${
          DashboardServerConfig.timezone
        }`
      : '-';

    const isOnline = DashboardHelpers.checkOnlineStatus(data.lastReading);
    const statusIcon = isOnline
      ? '<small class="mdi mdi-circle text-success"></small>'
      : '<small class="mdi mdi-circle text-danger"></small>';
    const statusText = data.deviceStatus || 'Offline';

    const waterLevel = lastData[0]
      ? `${DashboardHelpers.formatNumber(parseFloat(lastData[0]))} <sup>${
          data.unit
        }</sup>`
      : '-';

    const debit = lastData[1]
      ? `${DashboardHelpers.formatNumber(
          parseFloat(lastData[1]),
        )} <sup>m³/s</sup>`
      : '-';

    const warningBadge = DashboardHelpers.getWarningStatusBadge(data.status);

    if (DashboardServerConfig.organizationCode == 'ORG081') {
      return `
            <div class="table-responsive">
                <table class="table mb-2 font-12">
                    <tbody>
                        <tr>
                            <td class="px-0 py-2 pt-1 pe-3" colspan="3">
                                <div class="d-flex align-items-start">
                                    <img class="me-2 rounded-3" src="/images/defaults/${
                                      data.defaultImage || 'default.png'
                                    }" width="35" height="35" alt="${
                                      data.name
                                    }">
                                    <div class="w-100">
                                        <h5 class="mt-0 mb-1 fw-bold font-12" style="color: #2A62A6;">${
                                          data.name
                                        }</h5>
                                        <div class="d-flex align-items-center">
                                            <span>${statusIcon} ${statusText}</span>
                                            <span class="badge badge-outline-secondary rounded-1 ms-1">${
                                              data.deviceId
                                            }</span>
                                        </div>
                                    </div>
                                </div>
                            </td>
                        </tr>
                        <tr>
                            <td class="py-1 px-0">Sungai</td>
                            <td class="py-1 px-2">:</td>
                            <td class="py-1 px-0">${watershedName}</td>
                        </tr>
                        <tr>
                            <td class="py-1 px-0">Update</td>
                            <td class="py-1 px-2">:</td>
                            <td class="py-1 px-0">${readingAt}</td>
                        </tr>
                    </tbody>
                </table>
            </div>
            <div class="d-flex justify-content-around align-items-center mb-1 reading-text">
                <div class="col-4 text-center font-14 fw-bold text-dark">
                    ${waterLevel}
                </div>
                <div class="col-4 text-center font-14 fw-bold text-dark">
                    
                </div>
                <div class="col-4 text-center font-15 fw-bold text-dark">
                    ${warningBadge.html}
                </div>
            </div>
            <div class="d-flex justify-content-around reading-text mb-2">
                <div class="col-4 text-center font-12 text-nowrap">TMA</div>
                <div class="col-4 text-center font-12 text-nowrap"></div>
                <div class="col-4 text-center font-12 text-nowrap">Status</div>
            </div>
            <a href="/Station/Detail/${
              data.slug
            }" target="_blank" class="btn btn-sm btn-primary rounded-pill waves-effect waves-light text-white w-100">
                Lihat Detail
            </a>
        `;
    } else {
      return `
            <div class="table-responsive">
                <table class="table mb-2 font-12">
                    <tbody>
                        <tr>
                            <td class="px-0 py-2 pt-1 pe-3" colspan="3">
                                <div class="d-flex align-items-start">
                                    <img class="me-2 rounded-3" src="/images/defaults/${
                                      data.defaultImage || 'default.png'
                                    }" width="35" height="35" alt="${
                                      data.name
                                    }">
                                    <div class="w-100">
                                        <h5 class="mt-0 mb-1 fw-bold font-12" style="color: #2A62A6;">${
                                          data.name
                                        }</h5>
                                        <div class="d-flex align-items-center">
                                            <span>${statusIcon} ${statusText}</span>
                                            <span class="badge badge-outline-secondary rounded-1 ms-1">${
                                              data.deviceId
                                            }</span>
                                        </div>
                                    </div>
                                </div>
                            </td>
                        </tr>
                        <tr>
                            <td class="py-1 px-0">Sungai</td>
                            <td class="py-1 px-2">:</td>
                            <td class="py-1 px-0">${watershedName}</td>
                        </tr>
                        <tr>
                            <td class="py-1 px-0">Update</td>
                            <td class="py-1 px-2">:</td>
                            <td class="py-1 px-0">${readingAt}</td>
                        </tr>
                    </tbody>
                </table>
            </div>
            <div class="d-flex justify-content-around align-items-center mb-1 reading-text">
                <div class="col-4 text-center font-14 fw-bold text-dark">
                    ${waterLevel}
                </div>
                <div class="col-4 text-center font-14 fw-bold text-dark">
                    ${debit}
                </div>
                <div class="col-4 text-center font-15 fw-bold text-dark">
                    ${warningBadge.html}
                </div>
            </div>
            <div class="d-flex justify-content-around reading-text mb-2">
                <div class="col-4 text-center font-12 text-nowrap">TMA</div>
                <div class="col-4 text-center font-12 text-nowrap">Debit</div>
                <div class="col-4 text-center font-12 text-nowrap">Status</div>
            </div>
            <a href="/Station/Detail/${
              data.slug
            }" target="_blank" class="btn btn-sm btn-primary rounded-pill waves-effect waves-light text-white w-100">
                Lihat Detail
            </a>
        `;
    }
  };

  const _generateOwPopup = (data) => {
    moment.locale('id');
    const lastData = (data.lastData || '||').split('|');
    const watershedName = data.watershedName || '-';
    const readingAt = data.lastReading
      ? `${moment.parseZone(data.lastReading).format('D MMMM YYYY HH:mm')} ${
          DashboardServerConfig.timezone
        }`
      : '-';
    const isOnline = DashboardHelpers.checkOnlineStatus(data.lastReading);
    const statusIcon = isOnline
      ? '<small class="mdi mdi-circle text-success"></small>'
      : '<small class="mdi mdi-circle text-danger"></small>';
    const statusText = data.deviceStatus || 'Offline';
    const waterLevel = lastData[0]
      ? `${DashboardHelpers.formatNumber(lastData[0])} <sup>${data.unit || 'm'}</sup>`
      : '-';
    const elevation =
      lastData[1] && lastData[1] !== 'null'
        ? `${DashboardHelpers.formatNumber(lastData[1])} <sup>mdpl</sup>`
        : '-';

    return `
      <div class="table-responsive">
        <table class="table mb-2 font-12">
          <tbody>
            <tr>
              <td class="px-0 py-2 pt-1 pe-3" colspan="3">
                <div class="d-flex align-items-start">
                  <div class="w-100">
                    <h5 class="mt-0 mb-1 fw-bold font-12" style="color: #1565c0;">${data.name || '-'}</h5>
                    <div class="d-flex align-items-center">
                      <span>${statusIcon} ${statusText}</span>
                      <span class="badge badge-outline-secondary rounded-1 ms-1">${data.deviceId || '-'}</span>
                    </div>
                  </div>
                </div>
              </td>
            </tr>
            <tr><td class="py-1 px-0">DAS</td><td class="py-1 px-0">:</td><td class="py-1 px-0">${watershedName}</td></tr>
            <tr><td class="py-1 px-0">Update</td><td class="py-1 px-0">:</td><td class="py-1 px-0">${readingAt}</td></tr>
            <tr><td class="py-1 px-0">Water Level</td><td class="py-1 px-0">:</td><td class="py-1 px-0">${waterLevel}</td></tr>
            <tr><td class="py-1 px-0">Elevasi</td><td class="py-1 px-0">:</td><td class="py-1 px-0">${elevation}</td></tr>
          </tbody>
        </table>
        <a href="/station/detail/${data.slug}" target="_blank" class="btn btn-sm btn-primary w-100">Lihat Detail</a>
      </div>`;
  };

  const _generateTmatPopup = (data) => {
    moment.locale('id');
    console.log('Data TMAT', data);
    const lastData = (data.lastData || '|-|-').split('|');
    const watershedName = data.watershedName || '-';
    const readingAt = data.lastReading
      ? `${moment.parseZone(data.lastReading).format('D MMMM YYYY HH:mm')} ${
          DashboardServerConfig.timezone
        }`
      : '-';

    const isOnline = DashboardHelpers.checkOnlineStatus(data.lastReading);
    const statusIcon = isOnline
      ? '<small class="mdi mdi-circle text-success"></small>'
      : '<small class="mdi mdi-circle text-danger"></small>';
    const statusText = data.deviceStatus || 'Offline';

    //make function to make sure 1,44 return to 1.44
    const parseDecimal = (value) => {
      if (!value) return '-';
      const parsed = parseFloat(value.toString().replace(',', '.'));
      return isNaN(parsed) ? '-' : parsed;
    };

    const waterLevel = lastData[0]
      ? `${DashboardHelpers.formatNumber(parseDecimal(lastData[0]))} <sup>${
          data.unit
        }</sup>`
      : '-';

    const debit = lastData[1]
      ? `${DashboardHelpers.formatNumber(
          parseDecimal(lastData[1]),
        )} <sup>m³/s</sup>`
      : '-';

    const warningBadge = DashboardHelpers.getWarningStatusBadge(data.status);

    const wellElevationValue =
      parseDecimal(lastData[3]) - parseDecimal(lastData[0]);
    const wellElevation = isNaN(wellElevationValue)
      ? '-'
      : `${DashboardHelpers.formatNumber(wellElevationValue)} <sup>mdpl</sup>`;

    if (DashboardServerConfig.organizationCode == 'ORG084') {
      return `
            <div class="table-responsive">
                <table class="table mb-2 font-12">
                    <tbody>
                        <tr>
                            <td class="px-0 py-2 pt-1 pe-3" colspan="3">
                                <div class="d-flex align-items-start">
                                    <img class="me-2 rounded-3" src="/images/defaults/${
                                      data.defaultImage || 'default.png'
                                    }" width="35" height="35" alt="${
                                      data.name
                                    }">
                                    <div class="w-100">
                                        <h5 class="mt-0 mb-1 fw-bold font-12" style="color: #2A62A6;">${
                                          data.name
                                        }</h5>
                                        <div class="d-flex align-items-center">
                                            <span>${statusIcon} ${statusText}</span>
                                            <span class="badge badge-outline-secondary rounded-1 ms-1">${
                                              data.deviceId
                                            }</span>
                                        </div>
                                    </div>
                                </div>
                            </td>
                        </tr>
                        
                        <tr>
                            <td class="py-1 px-0">Update</td>
                            <td class="py-1 px-2">:</td>
                            <td class="py-1 px-0">${readingAt}</td>
                        </tr>
                    </tbody>
                </table>
            </div>
            <div class="d-flex justify-content-around align-items-center mb-1 reading-text">
                <div class="col-4 text-center font-14 fw-bold text-dark">
                    ${waterLevel}
                </div>
                <div class="col-4 text-center font-14 fw-bold text-dark">
                    ${wellElevation}
                </div>
            </div>
            <div class="d-flex justify-content-around reading-text mb-2">
                <div class="col-4 text-center font-12 text-nowrap">MAT</div>
                <div class="col-4 text-center font-12 text-nowrap">Elv. MAT</div>
       
            </div>
             <div class="d-flex justify-content-around align-items-center mb-0 reading-text">
                <div class="col-4 text-center font-14 fw-bold text-dark">
                    ${data.ph || '-'}
                </div>
                <div class="col-4 text-center font-14 fw-bold text-dark">
                    ${data.temperature || '-'} <sup>°C</sup>
                </div>
               
            </div>
            <div class="d-flex justify-content-around reading-text mb-3 mt-0">
                <div class="col-4 text-center font-12 text-nowrap">pH</div>
                <div class="col-4 text-center font-12 text-nowrap">Suhu</div>
       
            </div>
            <a href="/Station/Detail/${
              data.slug
            }" target="_blank" class="btn btn-sm btn-primary rounded-pill waves-effect waves-light text-white w-100">
                Lihat Detail
            </a>
        `;
    } else {
      return `
            <div class="table-responsive">
                <table class="table mb-2 font-12">
                    <tbody>
                        <tr>
                            <td class="px-0 py-2 pt-1 pe-3" colspan="3">
                                <div class="d-flex align-items-start">
                                    <img class="me-2 rounded-3" src="/images/defaults/${
                                      data.defaultImage || 'default.png'
                                    }" width="35" height="35" alt="${
                                      data.name
                                    }">
                                    <div class="w-100">
                                        <h5 class="mt-0 mb-1 fw-bold font-12" style="color: #2A62A6;">${
                                          data.name
                                        }</h5>
                                        <div class="d-flex align-items-center">
                                            <span>${statusIcon} ${statusText}</span>
                                            <span class="badge badge-outline-secondary rounded-1 ms-1">${
                                              data.deviceId
                                            }</span>
                                        </div>
                                    </div>
                                </div>
                            </td>
                        </tr>
                        
                        <tr>
                            <td class="py-1 px-0">Update</td>
                            <td class="py-1 px-2">:</td>
                            <td class="py-1 px-0">${readingAt}</td>
                        </tr>
                    </tbody>
                </table>
            </div>
            <div class="d-flex justify-content-around align-items-center mb-1 reading-text">
                <div class="col-4 text-center font-14 fw-bold text-dark">
                    ${waterLevel}
                </div>
                <div class="col-4 text-center font-14 fw-bold text-dark">
                    ${wellElevation}
                </div>
               
            </div>
            <div class="d-flex justify-content-around reading-text mb-2">
                <div class="col-4 text-center font-12 text-nowrap">MAT</div>
                <div class="col-4 text-center font-12 text-nowrap">Elv. MAT</div>
       
            </div>
            <a href="/Station/Detail/${
              data.slug
            }" target="_blank" class="btn btn-sm btn-primary rounded-pill waves-effect waves-light text-white w-100">
                Lihat Detail
            </a>
        `;
    }
  };

  /**
   * Generate ARR popup content
   */
  const _generateArrPopup = (data) => {
    moment.locale('id');

    console.log('[Popup ARR] marker data:', data);

    const readingAt = data.lastReading
      ? `${moment.parseZone(data.lastReading).format('D MMMM YYYY HH:mm')} ${
          DashboardServerConfig.timezone
        }`
      : '-';

    const isOnline = DashboardHelpers.checkOnlineStatus(data.lastReading);
    const statusIcon = isOnline
      ? '<small class="mdi mdi-circle text-success"></small>'
      : '<small class="mdi mdi-circle text-danger"></small>';
    const statusText = data.deviceStatus || 'Offline';

    const rainfallValue = _resolveRainfallLastHour(data, null);
    const intensityCode = _resolveIntensityLastHour(data, null);
    const rainfall = _formatRainfallMm(rainfallValue);
    const intensity =
      intensityCode != null
        ? DashboardHelpers.getIntensityIconMap(intensityCode)
        : '-';
    const intensityName =
      intensityCode != null
        ? DashboardHelpers.getIntensityName(intensityCode)
        : '-';

    console.log('[Popup ARR] rainfall/intensity:', {
      rainfallValue,
      intensityCode,
      rainfall,
      intensityName,
      lastData: data.lastData,
      status: data.status,
      rainfallLastHour: data.rainfallLastHour,
      intensityLastHour: data.intensityLastHour,
    });

    return `
            <div class="table-responsive">
                <table class="table mb-2 font-12">
                    <tbody>
                        <tr>
                            <td class="px-0 py-2 pt-1" colspan="3">
                                <div class="d-flex align-items-start">
                                    <img class="me-2 rounded-3" src="/images/defaults/${
                                      data.defaultImage || 'default.png'
                                    }" width="35" height="35" alt="${
                                      data.name
                                    }">
                                    <div class="w-100">
                                        <h5 class="mt-0 mb-1 fw-bold font-12" style="color: #2A62A6;">${
                                          data.name
                                        }</h5>
                                        <div class="d-flex align-items-center">
                                            <span>${statusIcon} ${statusText}</span>
                                            <span class="badge badge-outline-secondary rounded-1 ms-1">${
                                              data.deviceId
                                            }</span>
                                        </div>
                                    </div>
                                </div>
                            </td>
                        </tr>
                        <tr>
                            <td class="py-1 px-0">Update</td>
                            <td class="py-1 px-2">:</td>
                            <td class="py-1 px-0">${readingAt}</td>
                        </tr>
                    </tbody>
                </table>
            </div>
            <div class="d-flex justify-content-around align-items-center reading-text">
                <div class="col-6 text-center font-15 fw-bold text-dark">
                    ${rainfall}
                </div>
                <div class="col-6 text-center font-15 fw-bold text-dark">
                    ${intensity}
                </div>
            </div>
            <div class="d-flex justify-content-around reading-text mb-3">
                <div class="col-6 text-center font-12 text-nowrap">Curah Hujan</div>
                <div class="col-6 text-center font-12 text-nowrap">${intensityName}</div>
            </div>
            <a href="/Station/Detail/${
              data.slug
            }" target="_blank" class="btn btn-sm btn-primary rounded-pill waves-effect waves-light text-white w-100">
                Lihat Detail
            </a>
        `;
  };

  /**
   * Generate AWLR+ARR popup content
   */
  const _generateAwlrArrPopup = (data) => {
    moment.locale('id');

    console.log('[Popup AWLR_ARR] marker data:', data);

    const lastData = String(data.lastData || '||')
      .split('|')
      .map((part) => part.trim());
    const lastStatus = String(data.status ?? '')
      .split('|')
      .map((part) => part.trim());

    const readingAt = data.lastReading
      ? `${moment.parseZone(data.lastReading).format('D MMMM YYYY HH:mm')} ${
          DashboardServerConfig.timezone
        }`
      : '-';

    const isOnline = DashboardHelpers.checkOnlineStatus(data.lastReading);
    const statusIcon = isOnline
      ? '<small class="mdi mdi-circle text-success"></small>'
      : '<small class="mdi mdi-circle text-danger"></small>';
    const statusText = data.deviceStatus || 'Offline';

    const waterLevelValue = _toNumberOrNull(lastData[0]);
    const waterLevel =
      waterLevelValue != null
        ? `${DashboardHelpers.formatNumber(waterLevelValue)} <sup>${
            data.unit || data.unitDisplay || ''
          }</sup>`
        : '-';

    const debitValue = _toNumberOrNull(lastData[1]);
    const debit =
      debitValue != null
        ? `${DashboardHelpers.formatNumber(debitValue)} <sup>m³/s</sup>`
        : '-';

    const warningCode = _toIntOrNull(lastStatus[0]);
    const warningStatus =
      warningCode != null
        ? DashboardHelpers.getWarningStatusBadge(warningCode)
        : { html: '-' };

    const rainfallValue = _resolveRainfallLastHour(data, 2);
    const intensityCode = _resolveIntensityLastHour(data, 1);
    const rainfall = _formatRainfallMm(rainfallValue);
    const intensity =
      intensityCode != null
        ? DashboardHelpers.getIntensityIconMap(intensityCode)
        : '-';
    const intensityName =
      intensityCode != null
        ? DashboardHelpers.getIntensityName(intensityCode)
        : '-';

    console.log('[Popup AWLR_ARR] rainfall/intensity:', {
      rainfallValue,
      intensityCode,
      rainfall,
      intensityName,
      lastData: data.lastData,
      status: data.status,
      rainfallLastHour: data.rainfallLastHour,
      intensityLastHour: data.intensityLastHour,
    });

    if (DashboardServerConfig.organizationCode == 'ORG081') {
      return `
            <div class="table-responsive">
                <table class="table mb-1 font-12">
                    <tbody>
                        <tr>
                            <td class="px-0 py-2 pt-1" colspan="3">
                                <div class="d-flex align-items-start">
                                    <img class="me-2 rounded-3" src="/images/defaults/${
                                      data.defaultImage || 'default.png'
                                    }" width="35" height="35" alt="${
                                      data.name
                                    }">
                                    <div class="w-100">
                                        <h5 class="mt-0 mb-1 fw-bold font-12" style="color: #2A62A6;">${
                                          data.name
                                        }</h5>
                                        <div class="d-flex align-items-center">
                                            <span>${statusIcon} ${statusText}</span>
                                            <span class="badge badge-outline-secondary rounded-1 ms-1">${
                                              data.deviceId
                                            }</span>
                                        </div>
                                    </div>
                                </div>
                            </td>
                        </tr>
                        <tr>
                            <td class="py-1 px-0">Update</td>
                            <td class="py-1 px-2">:</td>
                            <td class="py-1 px-0">${readingAt}</td>
                        </tr>
                    </tbody>
                </table>
            </div>
            <div class="d-flex justify-content-around align-items-center reading-text">
                <div class="col-4 text-center font-15 fw-bold text-dark">${waterLevel}</div>
                
                <div class="col-4 text-center font-15 fw-bold text-dark">${
                  warningStatus.html
                }</div>
            </div>
            <div class="d-flex justify-content-around reading-text mb-2">
                <div class="col-4 text-center font-12 text-nowrap">TMA</div>
       
                <div class="col-4 text-center font-12 text-nowrap">Status</div>
            </div>
            <div class="d-flex justify-content-around align-items-center reading-text">
                <div class="col-6 text-center font-15 fw-bold text-dark">${rainfall}</div>
                <div class="col-6 text-center font-15 fw-bold text-dark">${intensity}</div>
            </div>
            <div class="d-flex justify-content-around reading-text mb-3">
                <div class="col-6 text-center font-12 text-nowrap">Curah Hujan</div>
                <div class="col-6 text-center font-12 text-nowrap">${intensityName}</div>
            </div>
            <a href="/Station/Detail/${
              data.slug
            }" target="_blank" class="btn btn-sm btn-primary rounded-pill waves-effect waves-light text-white w-100">
                Lihat Detail
            </a>
        `;
    } else {
      return `
            <div class="table-responsive">
                <table class="table mb-1 font-12">
                    <tbody>
                        <tr>
                            <td class="px-0 py-2 pt-1" colspan="3">
                                <div class="d-flex align-items-start">
                                    <img class="me-2 rounded-3" src="/images/defaults/${
                                      data.defaultImage || 'default.png'
                                    }" width="35" height="35" alt="${
                                      data.name
                                    }">
                                    <div class="w-100">
                                        <h5 class="mt-0 mb-1 fw-bold font-12" style="color: #2A62A6;">${
                                          data.name
                                        }</h5>
                                        <div class="d-flex align-items-center">
                                            <span>${statusIcon} ${statusText}</span>
                                            <span class="badge badge-outline-secondary rounded-1 ms-1">${
                                              data.deviceId
                                            }</span>
                                        </div>
                                    </div>
                                </div>
                            </td>
                        </tr>
                        <tr>
                            <td class="py-1 px-0">Update</td>
                            <td class="py-1 px-2">:</td>
                            <td class="py-1 px-0">${readingAt}</td>
                        </tr>
                    </tbody>
                </table>
            </div>
            <div class="d-flex justify-content-around align-items-center reading-text">
                <div class="col-4 text-center font-15 fw-bold text-dark">${waterLevel}</div>
                <div class="col-4 text-center font-15 fw-bold text-dark">${
                  debit ?? '-'
                }</div>
                <div class="col-4 text-center font-15 fw-bold text-dark">${
                  warningStatus.html
                }</div>
            </div>
            <div class="d-flex justify-content-around reading-text mb-2">
                <div class="col-4 text-center font-12 text-nowrap">TMA</div>
                <div class="col-4 text-center font-12 text-nowrap">Debit</div>
                <div class="col-4 text-center font-12 text-nowrap">Status</div>
            </div>
            <div class="d-flex justify-content-around align-items-center reading-text">
                <div class="col-6 text-center font-15 fw-bold text-dark">${rainfall}</div>
                <div class="col-6 text-center font-15 fw-bold text-dark">${intensity}</div>
            </div>
            <div class="d-flex justify-content-around reading-text mb-3">
                <div class="col-6 text-center font-12 text-nowrap">Curah Hujan</div>
                <div class="col-6 text-center font-12 text-nowrap">${intensityName}</div>
            </div>
            <a href="/Station/Detail/${
              data.slug
            }" target="_blank" class="btn btn-sm btn-primary rounded-pill waves-effect waves-light text-white w-100">
                Lihat Detail
            </a>
        `;
    }
  };

  /** Urutan lastData AWS: Humidity|Rainfall|Pressure|Temperature|SolarRadiation|WindDirection|WindSpeed|Evaporation|UV */
  const AWS_POPUP_SENSOR_KEYS = [
    'humidity',
    'rainfall',
    'pressure',
    'temperature',
    'solarradiation',
    'winddirection',
    'windspeed',
    'evaporation',
    'uv',
  ];

  const AWS_POPUP_FLAG_KEYS = [
    'isHumidity',
    'isRainfall',
    'isPressure',
    'isTemperature',
    'isSolarRadiation',
    'isWindDirection',
    'isWindSpeed',
    'isEvaporation',
    'isUv',
  ];

  /** Sensor AWS muncul hanya jika enabled di setting (sensors CSV / flag boolean). */
  const _isAwsPopupSensorEnabled = (data, index) => {
    const key = AWS_POPUP_SENSOR_KEYS[index];
    if (!key) return false;

    const sensorsRaw = data?.sensors;
    if (sensorsRaw != null && String(sensorsRaw).trim() !== '') {
      const enabled = String(sensorsRaw)
        .toLowerCase()
        .split(',')
        .map((s) => s.trim().replace(/_/g, ''))
        .filter(Boolean);
      return enabled.includes(key);
    }

    const flagKey = AWS_POPUP_FLAG_KEYS[index];
    if (flagKey && Object.prototype.hasOwnProperty.call(data || {}, flagKey)) {
      return data[flagKey] === true;
    }

    // Legacy marker tanpa setting: jangan tampilkan sensor kosong
    return true;
  };

  /**
   * Generate AWS popup content
   */
  const _generateAwsPopup = (data) => {
    moment.locale('id');

    console.log('[Popup AWS] marker data:', data);

    const lastData = String(data.lastData || '||||||||')
      .split('|')
      .map((part) => part.trim());
    const lastStatus = String(data.status ?? '')
      .split('|')
      .map((part) => part.trim());

    const readingAt = data.lastReading
      ? `${moment.parseZone(data.lastReading).format('D MMMM YYYY HH:mm')} ${
          DashboardServerConfig.timezone
        }`
      : '-';

    const isOnline = DashboardHelpers.checkOnlineStatus(data.lastReading);
    const statusIcon = isOnline
      ? '<small class="mdi mdi-circle text-success"></small>'
      : '<small class="mdi mdi-circle text-danger"></small>';
    const statusText = data.deviceStatus || 'Offline';

    // Parse sensors (order: Humidity, Rainfall, Pressure, Temperature, SolarRadiation, WindDirection, WindSpeed, Evaporation, UV)
    const sensorLabels = [
      'Kelembaban',
      'Curah Hujan',
      'Tekanan',
      'Suhu',
      'Radiasi Matahari',
      'Arah Angin',
      'Kecepatan Angin',
      'Penguapan',
      'UV',
    ];
    const sensorUnits = ['%', 'mm', 'hPa', '°C', 'W/m²', '°', 'm/s', 'mm', ''];

    const rainfallValue = _resolveRainfallLastHour(data, 1);
    const intensityCode = _resolveIntensityLastHour(data, 1);

    console.log('[Popup AWS] rainfall/intensity:', {
      rainfallValue,
      intensityCode,
      lastData: data.lastData,
      status: data.status,
      rainfallLastHour: data.rainfallLastHour,
      intensityLastHour: data.intensityLastHour,
      sensors: data.sensors,
    });

    let sensorsHtml = '';
    lastData.forEach((value, index) => {
      if (!_isAwsPopupSensorEnabled(data, index)) return;

      // Rainfall: pakai field langsung (sama seperti card), termasuk nilai 0
      if (index === 1) {
        if (rainfallValue == null) return;
        const intensityName =
          intensityCode != null
            ? DashboardHelpers.getIntensityName(intensityCode)
            : null;
        const intensityIcon =
          intensityCode != null
            ? DashboardHelpers.getIntensityIconMap(intensityCode)
            : '';
        const rainfallText = _formatRainfallMm(rainfallValue);
        const displayValue = intensityName
          ? `${rainfallText} ${intensityIcon} ${intensityName}`
          : rainfallText;

        sensorsHtml += `
                    <tr>
                        <td class="py-1 px-0">${sensorLabels[index]}</td>
                        <td class="py-1 px-2">:</td>
                        <td class="py-1 px-0">${displayValue}</td>
                    </tr>
                `;
        return;
      }

      if (value !== '' && value !== '-' && index < sensorLabels.length) {
        const numeric = _toNumberOrNull(value);
        const displayValue =
          numeric != null
            ? `${DashboardHelpers.formatNumber(numeric)} <sup>${sensorUnits[index]}</sup>`
            : `${value} <sup>${sensorUnits[index]}</sup>`;

        sensorsHtml += `
                    <tr>
                        <td class="py-1 px-0">${sensorLabels[index]}</td>
                        <td class="py-1 px-2">:</td>
                        <td class="py-1 px-0">${displayValue}</td>
                    </tr>
                `;
      }
    });

    return `
            <div class="table-responsive">
                <table class="table mb-1 font-12">
                    <tbody>
                        <tr>
                            <td class="px-0 py-2 pt-1" colspan="3">
                                <div class="d-flex align-items-start">
                                    <img class="me-2 rounded-3" src="/images/defaults/${
                                      data.defaultImage || 'default.png'
                                    }" width="35" height="35" alt="${
                                      data.name
                                    }">
                                    <div class="w-100">
                                        <h5 class="mt-0 mb-1 fw-bold font-12" style="color: #2A62A6;">${
                                          data.name
                                        }</h5>
                                        <div class="d-flex align-items-center">
                                            <span>${statusIcon} ${statusText}</span>
                                            <span class="badge badge-outline-secondary rounded-1 ms-1">${
                                              data.deviceId
                                            }</span>
                                        </div>
                                    </div>
                                </div>
                            </td>
                        </tr>
                        <tr>
                            <td class="py-1 px-0">Update</td>
                            <td class="py-1 px-2">:</td>
                            <td class="py-1 px-0">${readingAt}</td>
                        </tr>
                    </tbody>
                </table>
            </div>
            <div class="table-responsive">
                <table class="table mb-1 font-12">
                    <tbody>
                        ${sensorsHtml}
                    </tbody>
                </table>
            </div>
            <a href="/Station/Detail/${
              data.slug
            }" target="_blank" class="btn btn-sm btn-primary rounded-pill waves-effect waves-light text-white w-100">
                Lihat Detail
            </a>
        `;
  };

  /**
   * Generate Flow Meter popup content
   */
  const _generateFlowMeterPopup = (data) => {
    moment.locale('id');

    console.log('Data Flow Meter', data);

    const lastData = (data.lastData || '|-|-').split('|');

    const readingAt = data.lastReading
      ? `${moment.parseZone(data.lastReading).format('D MMMM YYYY HH:00')} ${
          DashboardServerConfig.timezone
        }`
      : '-';

    const isOnline = DashboardHelpers.checkOnlineStatus(data.lastReading);
    const statusIcon = isOnline
      ? '<small class="mdi mdi-circle text-success"></small>'
      : '<small class="mdi mdi-circle text-danger"></small>';
    const statusText = data.deviceStatus || 'Offline';

    const flowMonth = DashboardHelpers.formatFlowMeterValue(lastData[2], data);
    const flowTotal = DashboardHelpers.formatFlowMeterValue(lastData[1], data);

    return `
            <div class="table-responsive">
                <table class="table mb-2 font-12">
                    <tbody>
                        <tr>
                            <td class="px-0 py-2 pt-1" colspan="3">
                                <div class="d-flex align-items-start">
                                    <img class="me-2 rounded-3" src="/images/defaults/${
                                      data.defaultImage || 'default.png'
                                    }" width="35" height="35" alt="${
                                      data.name
                                    }">
                                    <div class="w-100">
                                        <h5 class="mt-0 mb-1 fw-bold font-12" style="color: #2A62A6;">${
                                          data.name
                                        }</h5>
                                        <div class="d-flex align-items-center">
                                            <span>${statusIcon} ${statusText}</span>
                                            <span class="badge badge-outline-secondary rounded-1 ms-1">${
                                              data.deviceId
                                            }</span>
                                        </div>
                                    </div>
                                </div>
                            </td>
                        </tr>
                        <tr>
                            <td class="py-1 px-0">Update</td>
                            <td class="py-1 px-2">:</td>
                            <td class="py-1 px-0">${readingAt}</td>
                        </tr>
                    </tbody>
                </table>
            </div>
            <div class="d-flex justify-content-around align-items-center reading-text">
                <div class="col-6 text-center font-15 fw-bold text-dark">${flowMonth}</div>
                <div class="col-6 text-center font-15 fw-bold text-dark">${flowTotal}</div>
            </div>
            <div class="d-flex justify-content-around reading-text mb-3">
                <div class="col-6 text-center font-12 text-nowrap">Volume Bulan Ini</div>
                <div class="col-6 text-center font-12 text-nowrap">Totalizer</div>
            </div>
            <a href="/Station/Detail/${
              data.slug
            }" target="_blank" class="btn btn-sm btn-primary rounded-pill waves-effect waves-light text-white w-100">
                Lihat Detail
            </a>
        `;
  };

  const _generateWaterQualityPopup = (data) => {
    moment.locale('id');

    const readingAt = data.lastReading
      ? `${moment.parseZone(data.lastReading).format('D MMMM YYYY HH:mm')} ${
          DashboardServerConfig.timezone
        }`
      : '-';

    const isOnline = DashboardHelpers.checkOnlineStatus(data.lastReading);
    const statusIcon = isOnline
      ? '<small class="mdi mdi-circle text-success"></small>'
      : '<small class="mdi mdi-circle text-danger"></small>';
    const statusText = data.deviceStatus || 'Offline';

    const sensorsHtml = _buildWqSensorRows(data);

    return `
            <div class="table-responsive">
                <table class="table mb-1 font-12">
                    <tbody>
                        <tr>
                            <td class="px-0 py-2 pt-1" colspan="3">
                                <div class="d-flex align-items-start">
                                    <img class="me-2 rounded-3" src="/images/defaults/${
                                      data.defaultImage || 'default.png'
                                    }" width="35" height="35" alt="${
                                      data.name
                                    }">
                                    <div class="w-100">
                                        <h5 class="mt-0 mb-1 fw-bold font-12" style="color: #2A62A6;">${
                                          data.name
                                        }</h5>
                                        <div class="d-flex align-items-center">
                                            <span>${statusIcon} ${statusText}</span>
                                            <span class="badge badge-outline-secondary rounded-1 ms-1">${
                                              data.deviceId
                                            }</span>
                                        </div>
                                    </div>
                                </div>
                            </td>
                        </tr>
                        <tr>
                            <td class="py-1 px-0">Update</td>
                            <td class="py-1 px-2">:</td>
                            <td class="py-1 px-0">${readingAt}</td>
                        </tr>
                    </tbody>
                </table>
            </div>
            <div class="table-responsive">
                <table class="table mb-1 font-12">
                    <tbody>
                        ${sensorsHtml}
                    </tbody>
                </table>
            </div>
            <a href="/Station/Detail/${
              data.slug
            }" target="_blank" class="btn btn-sm btn-primary rounded-pill waves-effect waves-light text-white w-100">
                Lihat Detail
            </a>
        `;
  };

  /**
   * AVWR popup — mirror left-bar card: header meta + last-reading table.
   */
  const _generateAvwrPopup = (data) => {
    moment.locale('id');

    const readingSource = data.lastReading || data.readingAt || null;
    const isOnline = DashboardHelpers.checkOnlineStatus(readingSource);
    const statusIcon = isOnline
      ? '<small class="mdi mdi-circle text-success me-1"></small>'
      : '<small class="mdi mdi-circle text-danger me-1"></small>';
    const statusText =
      data.deviceStatus || (isOnline ? 'Online' : 'Offline');

    let readingAt = '-';
    if (readingSource != null) {
      const today = moment().format('YYYY-MMM-DD');
      const readDate = moment.parseZone(readingSource).format('YYYY-MMM-DD');
      if (today === readDate) {
        readingAt = `Hari Ini, ${moment.parseZone(readingSource).format('HH:mm')}`;
      } else {
        readingAt = moment
          .parseZone(readingSource)
          .locale('id')
          .format('D MMMM YYYY HH:mm');
      }
      if (
        typeof DashboardServerConfig !== 'undefined' &&
        DashboardServerConfig.timezone
      ) {
        readingAt += ` ${DashboardServerConfig.timezone}`;
      }
    }

    const brand =
      data.brandName != null && String(data.brandName).trim() !== ''
        ? data.brandName
        : 'Unknown Brand';
    const deviceId = data.deviceId != null ? data.deviceId : '-';

    let batteryBadge = '';
    if (
      typeof DashboardServerConfig !== 'undefined' &&
      DashboardServerConfig.powerSource == 'B' &&
      data.battery != null
    ) {
      const batteryClass =
        data.battery < 7
          ? 'mdi-battery-charging-low'
          : 'mdi-battery-charging-high';
      batteryBadge = `
        <span class="badge badge-outline-secondary rounded-1 ms-1">
          <i class="mdi ${batteryClass}"></i> ${DashboardHelpers.formatNumber(data.battery)} V
        </span>`;
    }

    let offlineFooter = '';
    if (
      String(data.deviceStatus || '').toLowerCase() === 'offline' ||
      !isOnline
    ) {
      const message =
        readingSource == null
          ? 'Alat belum mengirim data'
          : 'Alat tidak aktif lebih dari 24 jam';
      offlineFooter = `
        <div class="mt-2 mb-2">
          <small class="m-0 d-flex align-items-center text-warning fw-normal" style="color: #ED8F2A;">
            <i class="mdi mdi-alert-circle-outline font-14 me-1"></i>
            ${message}
          </small>
        </div>`;
    }

    const imageName =
      data.defaultImage || data.photo || 'default.png';

    return `
      <div class="avwr-map-popup" style="min-width:260px;max-width:340px;">
        <div class="d-flex align-items-start mb-2">
          <img class="me-2 rounded-3" src="/images/defaults/${imageName}" width="35" height="35" alt="${DashboardHelpers.escapeHtml(data.name || '')}">
          <div class="w-100">
            <h5 class="mt-0 mb-1 fw-bold font-12 text-uppercase" style="color: #2A62A6;">
              ${statusIcon}${DashboardHelpers.escapeHtml(data.name || '-')}
            </h5>
            <div class="d-flex flex-wrap align-items-center gap-1">
              <span class="badge badge-outline-secondary rounded-1">${DashboardHelpers.escapeHtml(brand)}</span>
              <span class="badge badge-outline-secondary rounded-1">${DashboardHelpers.escapeHtml(deviceId)}</span>
              ${batteryBadge}
            </div>
            <div class="mt-1 font-12 text-muted">${statusText} · ${readingAt}</div>
          </div>
        </div>
        ${DashboardHelpers.renderAvwrLastReading(data)}
        ${offlineFooter}
        <a href="/Station/Detail/${data.slug}" target="_blank"
           class="btn btn-sm btn-primary rounded-pill waves-effect waves-light text-white w-100 mt-2">
          Lihat Detail
        </a>
      </div>
    `;
  };

  // Public API
  return {
    /**
     * Generate popup content based on station type
     */
    generate(station) {
      const stationType = (station.type || '').toUpperCase();
      switch (stationType) {
        case DashboardConstants.STATION_TYPES.AWLR:
        case 'AWLR':
          return _generateAwlrPopup(station);

        case DashboardConstants.STATION_TYPES.TMAT:
        case DashboardConstants.STATION_TYPES.AGWLR:
          return _generateTmatPopup(station);

        case DashboardConstants.STATION_TYPES.OW:
          return _generateOwPopup(station);

        case DashboardConstants.STATION_TYPES.ARR:
          return _generateArrPopup(station);

        case DashboardConstants.STATION_TYPES.AWLR_ARR:
          return _generateAwlrArrPopup(station);

        case DashboardConstants.STATION_TYPES.AWS:
          return _generateAwsPopup(station);

        case DashboardConstants.STATION_TYPES.FM:
          return _generateFlowMeterPopup(station);
        case DashboardConstants.STATION_TYPES.WQ:
        case 'WQMS':
          return _generateWaterQualityPopup(station);

        case DashboardConstants.STATION_TYPES.AVWR:
        case 'AVWR':
          return _generateAvwrPopup(station);

        default:
          return `
                <div class="alert alert-info">
                    <strong>${station.name}</strong><br>
                    Type: ${station.type}<br>
                    Device ID: ${station.deviceId}
                </div>
            `;
      }
    },
  };
})();

// Expose to global scope
window.PopupModule = PopupModule;
